import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EmberEventFn } from 'src/types/emberEventFn'

import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { SubjectReconciliationSuggestionCard } from '@features/paymentReconciliationModal/elements/SubjectReconciliationSuggestionCard'
import {
  useDeleteSubjectReconciliationSuggestion,
  useSubjectReconciliationSuggestions,
} from '@features/paymentReconciliationModal/hooks'
import { reconciliationSuggestHasDifference } from '@features/paymentReconciliationModal/utils'
import { notifyPaymentRegistrationError } from '@features/paymentReconciliationModal/utils/notifyPaymentRegistrationError'
import {
  BankMatchRejectionReason,
  BankMatchRejectionReasonValue,
  BankMatchUiFlow,
  UiSource,
} from '@features/reconciliation/enums'
import { useReconcileInvoice } from '@features/reconciliation/hooks/useReconcileInvoice'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../config/queryClient'
import { QueryKeys } from '../../../../enums/queryKeys'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { useInvoice } from '../../../invoices/hooks/useInvoice'

interface InvoiceOverviewReconciliationSuggestionContentProps {
  invoiceId: string
  openPaymentModal: EmberEventFn
}

export const InvoiceOverviewReconciliationSuggestionContent = ({
  invoiceId,
  openPaymentModal,
}: InvoiceOverviewReconciliationSuggestionContentProps): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { organization } = useUserOrganization()
  const { data: invoiceData, isLoading: isFetchingInvoice } = useInvoice(invoiceId)
  const { suggestions, isLoading: isLoadingSuggestions } = useSubjectReconciliationSuggestions(invoiceData?.invoice)

  const { deleteSuggestion, isProcessing: isProcessingDelete } = useDeleteSubjectReconciliationSuggestion({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.Invoice, invoiceId])
      queryClient.invalidateQueries([QueryKeys.InvoiceReconciliationMatches, invoiceId])
    },
    onError: () => {
      notifyPaymentRegistrationError(t)
    },
  })

  const handleDeleteSuggestion = useCallback(
    (rejectionReason?: BankMatchRejectionReasonValue) => {
      if (!organization?.id || !suggestions?.length) {
        return
      }
      deleteSuggestion({
        organizationId: organization?.id,
        suggestion: suggestions?.[0],
        uiSource: UiSource.InvoiceScreen,
        rejectionReason,
      })
    },
    [organization?.id, suggestions, deleteSuggestion],
  )

  const onSuccessReconciliation = useCallback(() => {
    /* eslint-disable @typescript-eslint/naming-convention */
    track('Invoice Payment Created (Client)', {
      invoice: invoiceId,
      is_auto_matched: true,
      payment_mode: 'pay and reconcile',
    })
    track('XXX - Reconciliation - Reconciliation Suggestion Accepted (Client)')

    /** After a successful reconciliation, we remove the suggestion from the database.
     * This prevents the same suggestion from being shown again if the payment is voided in the future.
     */
    handleDeleteSuggestion(BankMatchRejectionReason.SuggestionAccepted)
  }, [track, invoiceId, handleDeleteSuggestion])

  const reconciliationTransaction = useMemo(() => suggestions?.[0]?.transactions?.[0], [suggestions])
  const subjectAmount = invoiceData?.invoice?.balance
  const isCrossCurrency = useMemo(
    () => reconciliationTransaction?.currency !== invoiceData?.invoice?.currencyId,
    [reconciliationTransaction, invoiceData?.invoice?.currencyId],
  )
  const crossCurrencyPaymentAmount = isCrossCurrency ? subjectAmount : undefined
  const { reconcileSubject: reconcileInvoice, isProcessing: isProcessingReconcile } = useReconcileInvoice({
    invoiceId,
    organizationId: organization?.id,
    bankLineId: reconciliationTransaction?.id,
    crossCurrencyPaymentAmount,
    isAutoMatch: true,
    onSuccess: onSuccessReconciliation,
    uiSource: UiSource.InvoiceScreen,
    uiFlow: BankMatchUiFlow.Matched,
  })

  const hasDifference = reconciliationSuggestHasDifference(reconciliationTransaction, invoiceData?.invoice)
  const isLoading = isLoadingSuggestions || isFetchingInvoice
  const isProcessing = isProcessingDelete || isProcessingReconcile

  const handleReject = useCallback(() => {
    if (!organization?.id || !suggestions?.length) {
      return
    }

    track('XXX - Reconciliation - Reconciliation Suggestion Rejected (Client)')
    handleDeleteSuggestion()
  }, [suggestions, organization?.id, handleDeleteSuggestion, track])

  const handleAccept = useCallback(() => {
    if (hasDifference) {
      openPaymentModal({ detail: null })
      return
    }

    reconcileInvoice()
  }, [hasDifference, openPaymentModal, reconcileInvoice])

  if (!isAuthorized(Scope.BankReconciliationAutoMatch) || invoiceData?.invoice?.isPaid || isLoading) {
    return null
  }

  return (
    <SubjectReconciliationSuggestionCard
      handleAcceptSuggestion={handleAccept}
      handleRejectSuggestion={handleReject}
      isProcessingSuggestion={isProcessing}
      isProcessingDelete={isProcessingDelete}
      isProcessingReconcile={isProcessingReconcile}
      subjectType={InvoiceType.Invoice}
      suggestions={suggestions}
    />
  )
}
